<template>
  <div class="mb-50" :class="['app_record', {'one-line': oneLine}]" @click="openRecordModal(record.id)">
    <sensitive-data-wrapper>
      <p class="app_record--address">{{ address }}</p>
    </sensitive-data-wrapper>

    <div class="app_record--info">
      <div v-if="includeRole" class="app_record--info--item">
        <p class="app_record--status">Role: {{ role }}</p>
      </div>

      <div v-if="!hideStatusLabel" class="app_record--info--item">
        <p class="app_record--status">
          <span>{{ statusState }}: {{ statusLabel }}</span>

          <template v-if="showFollowUpLabel">
            <span v-if="isInAutomatedFollowUp" class="app_record--status--fus">Automated Sequence: {{ followUpLabel }}</span>
            <span v-else-if="isFollowUpState" class="app_record--status--fus">Not in automated sequence</span>
          </template>
        </p>
      </div>

      <div class="app_record--info--item">
        <b-tooltip
          v-if="temperature.label !== 'None'"
          :label="temperature.label"
          :position="tooltipPosition"
        >
          <icon-default
            icon="temperature"
            size="is-small"
            :class="['app_record--temp', temperature.color]"
          ></icon-default>
        </b-tooltip>

        <b-tooltip
          v-if="dealProbability.score"
          label="Deal Probability"
          :position="tooltipPosition"
        >
          <icon-default
            icon="deal-probability"
            size="is-small"
            :class="['app_record--probability', dealProbability.color]"
          ></icon-default>
        </b-tooltip>

        <b-tooltip
          v-if="record.campaign_id"
          :label="$store.getters.campaigns.find(c => c.id === record.campaign_id).label"
          :position="tooltipPosition"
        >
          <icon-default
            :icon="$utils.getCampaignIcon(record.campaign_id)"
            size="is-small"
          ></icon-default>
        </b-tooltip>

        <template v-if="!hideExtraIcons">
          <b-tooltip
            v-if="incompleteRequiredTasks.length"
            :label="`${incompleteRequiredTasks.length} required ${incompleteRequiredTasks.length > 1 ? 'tasks' : 'task'}`"
            :position="tooltipPosition"
          >
            <icon-default
              icon="task"
              size="is-small"
              color="is-danger"
            ></icon-default>
          </b-tooltip>

          <b-tooltip
            v-if="missCalls"
            :label="`${missCalls.count} missed calls`"
            :position="tooltipPosition"
          >
            <icon-default
              icon="call-missed"
              size="is-small"
              color="is-danger"
            ></icon-default>
          </b-tooltip>

          <app-avatar
            v-for="user in record.users"
            :key="user.id"
            :user-id="user.id"
            :size="22"
            :position="tooltipPosition"
          ></app-avatar>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {ENUMS} from "../../services/enums";
import router from "../../router";
import SensitiveDataWrapper from "../SensitiveDataWrapper.vue";

export default {
  name: "AppRecord",
  components: {SensitiveDataWrapper},
  props: {
    record: {
      type: Object,
      required: true
    },
    includeRole: {
      type: Boolean,
      default: false
    },
    contactId: {
      type: Number
    },
    oneLine: {
      type: Boolean,
      default: false
    },
    tooltipPosition: {
      type: String,
      default: 'is-right'
    },
    includeFusLabel: {
      type: Boolean,
      default: false
    },
    hideStatusLabel: {
      type: Boolean,
      default: false
    },
    hideExtraIcons: {
      type: Boolean,
      default: false
    },
  },
  computed: {
    address() {
      const address = this.record.property.line_1;
      const city = this.record.property.city;
      const state = this.record.property.state;

      if (address) {
        if (city && state) {
          return `${address}, ${city} ${state}`;
        }

        if (city) {
          return `${address}, ${city}`;
        }

        if (state) {
          return `${address}, ${state}`;
        }

        return address
      }

      if (!address) {
        if (city && state) {
          return `${city} ${state}`;
        }

        if (city) {
          return city;
        }

        if (state) {
          return state;
        }
      }

      return 'Unknown Street';
    },
    role() {
      if(this.record.buyer_id === this.contactId) {
        return 'Buyer';
      }

      if(this.record.seller_id === this.contactId) {
        return 'Seller';
      }

      return 'Other';
    },
    status() {
      return this.$store.getters['pipeline/getStatusById'](this.record.current_status_id)
    },
    statusState() {
      return this.$store.getters['pipeline/getStateLabelByStatusId'](this.record.current_status_id);
    },
    statusLabel() {
      return this.status.label;
    },
    showFollowUpLabel() {
      return this.$store.getters.account.has_follow_up_sequence_feature || this.includeFusLabel;
    },
    isFollowUpState() {
      return this.status.state === ENUMS.StatusStateEnum.FollowUp.value;
    },
    isInAutomatedFollowUp() {
      return this.isFollowUpState && this.record.activeFollowUpSequence;
    },
    followUpLabel() {
      if(this.record.activeFollowUpSequence) {
        const template = this.$store.getters.fusTemplateById(this.record.activeFollowUpSequence.FUS_template_id);

        return template.label;
      }
    },
    temperature() {
      const label = this.$utils.getEnumLabelDeprecated('temperatureEnum', this.record.temperature);
      const color = label.toLowerCase();

      return { color, label };
    },
    dealProbability() {
      const score = this.record.deal_probability_score;

      let color = 'red';
      if (score > 1 && score <= 3) color = 'yellow';
      if (score >= 4) color = 'green';

      return { color, score };
    },
    incompleteRequiredTasks() {
      return this.$store.getters.pipelineIncompleteTasks.filter(a => a.record_id === this.record.id && a.is_required_to_change_status);
    },
    missCalls() {
      return false;
    },
  },
  methods: {
    openRecordModal(id) {
      this.$utils.closeContactModal();
      router.replace({query: {lead: id}});
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/variables/variables";

.app_record {
  cursor: pointer;

  &:hover {

    .app_record--address {
      color: $primary;
    }
  }

  &--address {
    margin-bottom: 8px;
    font-weight: bold;
    color: $secondary;
  }

  &--info {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &--status {
    font-size: 0.75rem;
    color: $text-muted;

    &--fus {
      display: block;
      margin-top: 6px;
      line-height: 1.15;
    }
  }

  &--temp {

    &.cold {
      color: $blue;
    }

    &.warm {
      color: $yellow;
    }

    &.hot {
      color: $red;
    }
  }

  &--probability {

    &.red {
      color: $danger;
    }

    &.yellow {
      color: $warning;
    }

    &.green {
      color: $success;
    }
  }

  &.one-line {
    display: flex;
    align-items: center;

    .app_record--address {
      margin-bottom: 0;
    }

    .app_record--info--item {
      margin-left: 10px;
    }

    @include mobile {
      display: block;

      .app_record--address {
        margin-bottom: 8px;
      }

      .app_record--info--item {
        margin-left: 0;
      }
    }
  }
}

</style>
